import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Wand2, FileText, Trash2, Save, Plus } from 'lucide-react';

/**
 * Admin · Video Centre CMS
 *
 * List every video (draft + published). Create + edit inline. Two magic
 * buttons per row: "Generate transcript" (Whisper) and "Generate content"
 * (Claude Sonnet 4.6 or GPT-5.4 — admin picks).
 */
const CATEGORIES = [
    'neighbourhood-tours','market-updates','community-guides','investment-analysis',
    'restaurants','schools','parks','luxury-living','builders','development-updates',
];

const EMPTY = {
    slug: '', title: '', dek: '', category: 'neighbourhood-tours',
    thumbnail: '', duration_sec: 0, youtube_id: '', video_url: '',
    tags: [], related_neighbourhood_slugs: [], related_calculators: [],
    related_reports: [], transcript: '', article_md: '', faqs: [],
    status: 'draft',
};

export default function AdminVideos() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null);
    const [showNew, setShowNew] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-videos'],
        queryFn: async () => (await api.get('/admin/videos')).data,
        enabled: !!user?.is_admin,
        retry: false,
    });

    if (!user) return <div className="p-12 text-center text-neutral-500">Sign in to access.</div>;
    if (!user.is_admin) return <div className="p-12 text-center text-neutral-500">Admin only.</div>;

    const videos = data?.items || [];

    const refresh = () => qc.invalidateQueries({ queryKey: ['admin-videos'] });

    return (
        <div className="min-h-screen bg-white">
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex items-end justify-between">
                    <div>
                        <div className="overline">Admin</div>
                        <h1 className="font-editorial text-[42px] tracking-editorial mt-3 leading-tight">Video Centre CMS</h1>
                        <p className="mt-3 text-[14px] text-neutral-600 max-w-xl">
                            Paste a video URL, click auto-generate, edit if needed, publish. Transcripts via Whisper, articles via Claude/GPT.
                        </p>
                    </div>
                    <button
                        onClick={() => { setEditing({ ...EMPTY }); setShowNew(true); }}
                        data-testid="admin-video-new"
                        className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-3 text-[13px] font-medium hover:bg-neutral-800"
                       
                    >
                        <Plus className="w-4 h-4" /> New video
                    </button>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                {isLoading && <div className="text-neutral-500">Loading…</div>}
                {error && <div className="text-red-600 text-sm">Error loading videos.</div>}
                <div className="border-l border-t border-neutral-200">
                    {videos.map((v) => (
                        <VideoRow key={v.slug} v={v} onEdit={() => setEditing(v)} onChange={refresh} />
                    ))}
                </div>
            </section>

            {(editing || showNew) && (
                <VideoEditor
                    video={editing}
                    isNew={showNew}
                    onClose={() => { setEditing(null); setShowNew(false); }}
                    onSaved={() => { refresh(); setEditing(null); setShowNew(false); }}
                />
            )}
        </div>
    );
}


function VideoRow({ v, onEdit, onChange }) {
    const del = async () => {
        if (!confirm(`Delete "${v.title}"?`)) return;
        try {
            await api.delete(`/admin/videos/${v.slug}`);
            toast.success('Deleted');
            onChange();
        } catch (e) {
            toast.error('Delete failed');
        }
    };
    return (
        <div className="border-r border-b border-neutral-200 p-5 flex items-center justify-between gap-6" data-testid={`admin-row-${v.slug}`}>
            <div className="flex items-center gap-4 min-w-0">
                <div className="w-24 h-14 bg-neutral-200 shrink-0 overflow-hidden">
                    {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                    <div className="font-editorial text-[18px] tracking-editorial leading-tight truncate">{v.title}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono-data">
                        {v.category} · {v.status}
                        {v.transcript && <span className="ml-2 text-emerald-700">· transcript</span>}
                        {v.article_md && <span className="ml-2 text-emerald-700">· article</span>}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button onClick={onEdit} className="border border-neutral-300 px-3 py-1.5 text-[12px] hover:border-brand-navy">Edit</button>
                <button onClick={del} className="border border-red-200 text-red-700 px-3 py-1.5 text-[12px] hover:border-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}


function VideoEditor({ video, isNew, onClose, onSaved }) {
    const [f, setF] = useState({
        ...EMPTY,
        ...video,
        tags: Array.isArray(video?.tags) ? video.tags.join(', ') : (video?.tags || ''),
        related_neighbourhood_slugs: Array.isArray(video?.related_neighbourhood_slugs) ? video.related_neighbourhood_slugs.join(', ') : (video?.related_neighbourhood_slugs || ''),
        related_calculators: Array.isArray(video?.related_calculators) ? video.related_calculators.join(', ') : (video?.related_calculators || ''),
    });
    const [busy, setBusy] = useState(false);
    const [model, setModel] = useState('claude-sonnet-4-6');
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                ...f,
                duration_sec: Number(f.duration_sec) || 0,
                tags: typeof f.tags === 'string' ? f.tags.split(',').map((t) => t.trim()).filter(Boolean) : f.tags,
                related_neighbourhood_slugs: typeof f.related_neighbourhood_slugs === 'string' ? f.related_neighbourhood_slugs.split(',').map((t) => t.trim()).filter(Boolean) : f.related_neighbourhood_slugs,
                related_calculators: typeof f.related_calculators === 'string' ? f.related_calculators.split(',').map((t) => t.trim()).filter(Boolean) : f.related_calculators,
            };
            if (isNew) {
                await api.post('/admin/videos', payload);
            } else {
                await api.put(`/admin/videos/${video.slug}`, payload);
            }
            toast.success('Saved');
            onSaved();
        } catch (e) {
            toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const genTranscript = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/videos/${f.slug}/generate-transcript`);
            set('transcript', data.transcript);
            toast.success(`Transcript generated (${Math.round((data.audio_bytes || 0) / 1024)} KB)`);
        } catch (e) {
            toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Whisper failed');
        } finally {
            setBusy(false);
        }
    };

    const genContent = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/videos/${f.slug}/generate-content`, { model });
            const g = data.generated;
            set('article_md', g.article_md);
            set('faqs', g.faqs);
            set('tags', Array.isArray(g.tags) ? g.tags.join(', ') : g.tags);
            toast.success(`Content generated with ${g.content_model_used}`);
        } catch (e) {
            toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'LLM failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-video-editor">
            <div className="w-full max-w-4xl bg-white border border-neutral-200 my-8">
                <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-editorial text-[22px] tracking-editorial">{isNew ? 'New video' : `Edit · ${video.title}`}</div>
                    <button onClick={onClose} className="text-neutral-500 hover:text-black text-[13px]">Close</button>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input label="Slug" value={f.slug} onChange={(v) => set('slug', v)} disabled={!isNew} testid="admin-video-slug" />
                        <SelectField label="Category" value={f.category} onChange={(v) => set('category', v)} options={CATEGORIES} testid="admin-video-category" />
                    </div>
                    <Input label="Title" value={f.title} onChange={(v) => set('title', v)} testid="admin-video-title" />
                    <Input label="Dek (subtitle)" value={f.dek} onChange={(v) => set('dek', v)} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input label="YouTube ID (11 chars)" value={f.youtube_id || ''} onChange={(v) => set('youtube_id', v)} testid="admin-video-yt" />
                        <Input label="Direct video URL (mp4/mp3 — enables Whisper)" value={f.video_url || ''} onChange={(v) => set('video_url', v)} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input label="Thumbnail URL" value={f.thumbnail || ''} onChange={(v) => set('thumbnail', v)} />
                        <Input label="Duration (sec)" value={f.duration_sec} onChange={(v) => set('duration_sec', v)} type="number" />
                    </div>
                    <Input label="Tags (comma separated)" value={f.tags} onChange={(v) => set('tags', v)} />
                    <Input label="Related neighbourhood slugs (comma separated)" value={f.related_neighbourhood_slugs} onChange={(v) => set('related_neighbourhood_slugs', v)} />
                    <Input label="Related calculators (affordability, invest, match)" value={f.related_calculators} onChange={(v) => set('related_calculators', v)} />

                    {/* Auto-generate row */}
                    {!isNew && (
                        <div className="border border-neutral-200 bg-[#FAFAF8] p-4">
                            <div className="overline">Auto-generate</div>
                            <div className="mt-4 flex flex-wrap gap-3 items-center">
                                <button
                                    onClick={genTranscript}
                                    disabled={busy || !f.video_url}
                                    className="inline-flex items-center gap-2 border border-neutral-400 bg-white px-4 py-2 text-[12.5px] hover:border-brand-navy disabled:opacity-40"
                                   
                                    data-testid="admin-gen-transcript"
                                >
                                    <FileText className="w-4 h-4" /> Whisper transcript
                                </button>
                                <select
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    className="border border-neutral-300 px-3 py-2 text-[12.5px] bg-white"
                                   
                                >
                                    <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                                    <option value="gpt-5.4">GPT-5.4</option>
                                </select>
                                <button
                                    onClick={genContent}
                                    disabled={busy || !f.transcript}
                                    className="inline-flex items-center gap-2 bg-brand-navy text-white px-4 py-2 text-[12.5px] hover:bg-neutral-800 disabled:opacity-40"
                                   
                                    data-testid="admin-gen-content"
                                >
                                    <Wand2 className="w-4 h-4" /> Generate article + FAQs
                                </button>
                            </div>
                        </div>
                    )}

                    <TextArea label="Transcript" value={f.transcript} onChange={(v) => set('transcript', v)} rows={6} />
                    <TextArea label="Article (Markdown)" value={f.article_md} onChange={(v) => set('article_md', v)} rows={10} />
                    <div>
                        <div className="overline mb-2">FAQs (edit as JSON)</div>
                        <textarea
                            value={JSON.stringify(f.faqs || [], null, 2)}
                            onChange={(e) => { try { set('faqs', JSON.parse(e.target.value)); } catch (_) { /* accept while typing */ } }}
                            rows={6}
                            className="w-full border border-neutral-300 px-3 py-2 font-mono text-[12px] leading-relaxed"
                           
                        />
                    </div>
                    <SelectField label="Status" value={f.status} onChange={(v) => set('status', v)} options={['draft', 'published']} />

                    <div className="pt-4 border-t border-neutral-200 flex items-center justify-between">
                        <div className="text-[11px] text-neutral-500 font-mono-data uppercase tracking-[0.18em]">
                            {f.status === 'published' ? 'Publishing will make this live on /videos.' : 'Drafts are admin-only.'}
                        </div>
                        <button
                            onClick={save}
                            disabled={busy || !f.slug || !f.title}
                            data-testid="admin-video-save"
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 text-[13px] font-medium hover:bg-neutral-800 disabled:opacity-40"
                           
                        >
                            <Save className="w-4 h-4" /> Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


const Input = ({ label, value, onChange, type = 'text', disabled, testid }) => (
    <label className="block">
        <div className="overline mb-1.5">{label}</div>
        <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testid}
            className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-black disabled:bg-neutral-100"
           
        />
    </label>
);

const TextArea = ({ label, value, onChange, rows = 4 }) => (
    <label className="block">
        <div className="overline mb-1.5">{label}</div>
        <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] leading-relaxed focus:outline-none focus:border-black"
           
        />
    </label>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
    <label className="block">
        <div className="overline mb-1.5">{label}</div>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-black bg-white"
           
        >
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    </label>
);
